<template>
  <b-modal
    ref="modal"
    centered
    size="xl"
    body-class="pb-0"
    :hide-footer="initializing"
    footer-class="d-block pb-0"
    :title="$t('transports.transportRecordUploadInfo')"
    @hide="resetModal"
  >
    <div v-if="initializing" class="text-center mb-3">
      <font-awesome-icon spin icon="circle-notch" class="text-moremuted display-2"/>
    </div>

    <template v-else>
      <b-row>
        <b-col lg="3" md="6">
          <b-form-group>
            <b-input-group :append="stock.model.product.type.unit.name">
              <b-input-group-prepend is-text>
                {{ $t('transports.emptyWeight') }}
                <required-symbol/>
              </b-input-group-prepend>
              
              <numeric-input
                :name="$t('transports.emptyWeight')"
                v-validate="'required'"
                v-model="model.emptyWeight"
              />
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col lg="3" md="6">
          <b-form-group>
            <b-input-group>
              <b-input-group-prepend is-text>
                {{ $t('transports.weighbridgeReceipt') }}
                <required-symbol/>
              </b-input-group-prepend>
              
              <numeric-input
                :name="$t('transports.weighbridgeReceipt')"
                v-validate="'required'"
                v-model="model.emptyWeighbridgeReceipt"
              />
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col lg="3" md="6">
          <b-form-group>
            <date-picker
              required
              :label="$t('transports.date')"
              :name="$t('transports.date')"
              v-validate="'required'"
              v-model="model.emptyDate"
            />
          </b-form-group>
        </b-col>

        <b-col lg="3" md="6">
          <b-form-group>
            <b-input-group class="bg-white">
              <b-input-group-prepend is-text>
                {{ $t('transports.weighingHour') }}
                <required-symbol/>
              </b-input-group-prepend>

              <b-form-input
                class="text-center"
                :name="$t('transports.emptyWeighingMinutes')"
                v-validate="'required|min_value:0|max_value:59|min:2|integer'"
                v-model="model.emptyWeighingHour.minutes"
              />

              <div class="px-2 d-flex align-items-center border-top border-bottom input-border-color">
                :
              </div>

              <b-form-input
                class="text-center"
                :name="$t('transports.emptyWeighingHours')"
                v-validate="'required|min_value:0|max_value:23|min:2|integer'"
                v-model="model.emptyWeighingHour.hours"
              />
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="3" md="6">
          <b-form-group>
            <b-input-group :append="stock.model.product.type.unit.name">
              <b-input-group-prepend is-text>
                {{ $t('transports.fullWeight') }}
                <required-symbol/>
              </b-input-group-prepend>

              <numeric-input
                :name="$t('transports.fullWeight')"
                v-validate="'required'"
                v-model="model.fullWeight"
              />
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col lg="3" md="6">
          <b-form-group>
            <b-input-group>
              <b-input-group-prepend is-text>
                {{ $t('transports.weighbridgeReceipt') }}
                <required-symbol/>
              </b-input-group-prepend>
              
              <numeric-input
                :name="$t('transports.weighbridgeReceipt')"
                v-validate="'required'"
                v-model="model.fullWeighbridgeReceipt"
              />
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col lg="3" md="6">
          <b-form-group>
            <date-picker
              :label="$t('transports.date')"
              required
              :name="$t('transports.date')"
              v-validate="'required'"
              v-model="model.fullDate"
            />
          </b-form-group>
        </b-col>

        <b-col lg="3" md="6">
          <b-form-group>
            <b-input-group class="bg-white">
              <b-input-group-prepend is-text>
                {{ $t('transports.weighingHour') }}
                <required-symbol/>
              </b-input-group-prepend>
              
              <b-form-input
                class="text-center"
                :name="$t('transports.fullWeighingMinutes')"
                v-validate="'required|min_value:0|max_value:59|min:2|integer'"
                v-model="model.fullWeighingHour.minutes"
              />

              <div class="px-2 d-flex align-items-center border-top border-bottom input-border-color">
                :
              </div>

              <b-form-input
                class="text-center"
                :name="$t('transports.fullWeighingHours')"
                v-validate="'required|min_value:0|max_value:23|min:2|integer'"
                v-model="model.fullWeighingHour.hours"
              />
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="3" md="6">
          <b-form-group>
            <b-input-group :append="stock.model.product.type.unit.name">
              <b-input-group-prepend is-text>
                {{ $t('transports.actualWeight') }}
                <required-symbol/>
              </b-input-group-prepend>

              <numeric-input
                :min="0"
                :name="$t('transports.actualWeight')"
                v-validate="'required'"
                v-model="model.actualWeight"
              />
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group>
            <b-input-group :append="stock.model.product.type.unit.name">
              <b-input readonly :value="model.actualWeight && model.actualWeight.toPersianLetter()"/>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col lg="3" md="6">
          <b-form-group>
            <b-input-group>
              <b-input-group-prepend is-text>
                {{ $t('transports.bundleCount') }}
                <required-symbol/>
              </b-input-group-prepend>
              
              <numeric-input
                :name="$t('transports.bundleCount')"
                v-validate="'required'"
                v-model="model.bundleCount"
              />
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group>
        <uploader
          label="وزن باسکول"
          accept=".png,.jpg,.jpeg,.gif,.tiff,.pdf"
          v-model="model.image"
        />
      </b-form-group>
    </template>

    <template v-slot:modal-footer>
      <b-alert :show="errors.any()" variant="danger" class="px-4">
        <ul class="m-0 px-1">
          <li v-for="err in errors.all()" :key="err">{{err}}</li>
        </ul>
      </b-alert>

      <b-row>
        <b-col>
          <b-btn :disabled="progressing" @click.prevent="submit" variant="success" class="mb-3" block size="lg">
            <font-awesome-icon
              :icon="progressing ? 'cog' : 'check'"
              :spin="progressing"
              class="align-middle"
              :class="{'mr-1': $dir.ltr, 'ml-1': $dir.rtl}"
            />
            {{ $t('shared.save') }}
          </b-btn>
        </b-col>

        <b-col v-if="showPrintButton" md="6">
          <b-btn
            :to="`/transports/${transport.id}/licenses/${licenseId}/license-record`"
            target="_blank"
            class="mb-3"
            block
            size="lg"
          >
            <font-awesome-icon
              icon="print"
              class="align-middle"
              :class="{'mr-1': $dir.ltr, 'ml-1': $dir.rtl}"
            />

            {{ $t('shared.print') }}
          </b-btn>
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
import { DatePicker, Uploader } from '@/components/shared'

export default {
  name: 'LicenseRecordUploaded',
  components: {
    DatePicker,
    Uploader
  },
  data () {
    return {
      initializing: true,
      progressing: false,
      stock: null,
      transport: null,
      licenseId: null,
      baseModel: {
        actualWeight: null,
        bundleCount: null,
        emptyDate: null,
        emptyWeighbridgeReceipt: null,
        emptyWeighingHour: {
          hours: null,
          minutes: null
        },
        emptyWeight: null,
        fullDate: null,
        fullWeighbridgeReceipt: null,
        fullWeighingHour: {
          hours: null,
          minutes: null
        },
        fullWeight: null,
        image: null
      },
      model: null,
      showPrintButton: false
    }
  },
  watch: {
    'model.emptyWeight' (val) {
      this.model.actualWeight = this.model.fullWeight - val
    }
  },
  methods: {
    show (stock, transport, licenseId) {
      this.stock = stock
      this.transport = transport
      this.licenseId = licenseId
      this.baseModel.fullWeight = this.transport.bundlesCount * this.stock.bundleRate
      this.$refs.modal.show()

      this.getModel()
    },
    resetModal () {
      this.initializing = true
      this.progressing = false
      this.transport = null
      this.showPrintButton = false
      this.model = Object.assign({}, this.baseModel)
    },
    getModel () {
      this.$api.transports.getRecord(this.transport.id, this.licenseId)
      .then(res => {
        if (res) {
          this.model = Object.assign({}, res)

          this.showPrintButton = true

          if (this.model.emptyWeighingHour) {
            const data = this.model.emptyWeighingHour.split(':')

            const result = {
              hours: data[0],
              minutes: data[1]
            }

            this.model.emptyWeighingHour = result
          }

          if (this.model.fullWeighingHour) {
            const data = this.model.fullWeighingHour.split(':')

            const result = {
              hours: data[0],
              minutes: data[1]
            }

            this.model.fullWeighingHour = result
          }
        } else {
          this.model = Object.assign({}, this.baseModel)
        }

        this.initializing = false
      })
      .catch(err => {
        this.$showError(this.$getLocaleErrorMessage(err, 'transports'))
        this.$refs.modal.hide()
      })
    },
    getUserDisplayName (user) {
      return this.$api.account.getUserDisplayName(user)
    },
    submit () {
      this.progressing = true

      const data = Object.assign({}, this.model)
      data.emptyWeighingHour = `${data.emptyWeighingHour.hours}:${data.emptyWeighingHour.minutes}`
      data.fullWeighingHour = `${data.fullWeighingHour.hours}:${data.fullWeighingHour.minutes}`

      this.$api.transports.updateRecord(this.transport.id, this.licenseId, data)
      .then(res => {
        this.model.image = res.image
        this.progressing = false
        this.showPrintButton = true
        this.$showSuccess(this.$t('transports.transportRecordSaved'))
        this.$validator.reset()
      })
      .catch(err => {
        this.$showError(this.$getLocaleErrorMessage(err, 'transports'))
        this.$refs.modal.hide()
      })
    }
  }
}
</script>
